import { useQuery } from "@tanstack/react-query";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Calendar, ArrowRight } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import { blogPostsApi, type BlogPost } from "@/integrations/mysql/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { usePageContent } from "@/hooks/usePageContent";

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.substring(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function PostCard({ post }: { post: BlogPost }) {
  const category = post.blog_categories;
  const tagStyle = category
    ? { backgroundColor: category.color_hex + "22", color: category.color_hex }
    : { backgroundColor: "#1F593822", color: "#1F5938" };

  const slug = post.slug ?? post.id;

  return (
    <Link
      to={`/blog/${slug}`}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group flex flex-row md:flex-col h-full"
    >
      {/* Image */}
      <div className="relative shrink-0 w-28 h-full md:w-full md:h-48 overflow-hidden">
        {post.featured_image_url ? (
          <img
            src={post.featured_image_url}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full min-h-[100px] bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 flex items-center justify-center">
            <span className="text-3xl opacity-30">🥗</span>
          </div>
        )}
        {category && (
          <span
            className="absolute top-2 left-2 text-[10px] font-bold font-primary px-2 py-0.5 rounded-full backdrop-blur-sm hidden md:inline-flex"
            style={tagStyle}
          >
            {category.name}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-grow min-w-0">
        {/* Category — mobile only, shown as text row */}
        {category && (
          <span
            className="text-[10px] font-bold font-primary px-2 py-0.5 rounded-full self-start mb-2 md:hidden"
            style={tagStyle}
          >
            {category.name}
          </span>
        )}
        <h3 className="text-sm font-bold text-gray-800 leading-snug font-primary line-clamp-2 mb-1.5">
          {post.title}
        </h3>
        <p className="text-gray-500 text-xs leading-relaxed font-primary font-light line-clamp-2 flex-grow hidden sm:block">
          {post.excerpt ?? post.subtitle ?? ""}
        </p>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
          <span className="flex items-center text-gray-400 text-[11px] font-primary gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(post.created_at)}
          </span>
          <span className="text-brand-primary font-bold text-[11px] font-primary flex items-center gap-0.5">
            Ler <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function BlogCarousel({ className = "bg-white" }: { className?: string }) {
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog-carousel"],
    queryFn: () => blogPostsApi.list({ published: true, limit: 8 }),
    staleTime: 60_000,
  });
  const { getText } = usePageContent('home');
  const sectionLabel = getText('blog', 'label',     'Blog');
  const title        = getText('blog', 'title',     'Últimas do Blog');
  const linkText     = getText('blog', 'link_text', 'Ver todos os artigos');
  const linkUrl      = getText('blog', 'link_url',  '/blog');

  return (
    <section className={`py-20 ${className}`}>
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="section-label mb-2">{sectionLabel}</p>
            <h2 className="section-heading">{title}</h2>
          </div>
          <a href={linkUrl} className="text-brand-primary font-bold font-primary text-sm flex items-center gap-1 hover:text-brand-secondary transition-colors">
            {linkText} <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-80 rounded-2xl" />)}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">Nenhum post publicado ainda.</div>
        ) : (
          <Carousel
            className="w-full max-w-5xl mx-auto"
            plugins={[Autoplay({ delay: 4000 })]}
          >
            <CarouselContent>
              {posts.map((post) => (
                <CarouselItem key={post.id} className="md:basis-1/2 lg:basis-1/3">
                  <PostCard post={post} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="border-brand-primary/20 text-brand-primary hover:bg-brand-primary hover:text-white" />
            <CarouselNext className="border-brand-primary/20 text-brand-primary hover:bg-brand-primary hover:text-white" />
          </Carousel>
        )}
      </div>
    </section>
  );
}
